import FilledButtonM from "@common/components/button/FilledButtonM"
import IconButtonM from "@common/components/button/IconButtonM"
import { TabSelect } from "@common/components/select/TabSelect"
import TextM from "@common/components/text/TextM"
import { theme } from "@common/theme/theme"
import { DimensionsContext } from "@common/utils/mobileUtils"
import { useSystemBarInsets } from "@common/systemBars/SystemBarsFrame"
import {
    SUMMER_COURSES_DEFAULT_CONFIG,
    SUMMER_COURSES_OFFER_TYPE,
    SUMMER_COURSES_PRIVATE_HOURS_PER_WEEK,
    SUMMER_MONTH_CHOICE,
    SUMMER_PRICES,
    SUMMER_SHARED_TIMESLOT_SLOTS,
    computeSummerPauseCommitmentShiftForMonth,
    getSummerCoursesDefaultSlots,
    getSummerCoursesHoursPerWeek,
    getSummerCoursesPriceDescriptor,
    getSummerMonthDateRange,
    getSummerMonthLabel,
    getSummerSharedTimeslotsByDay,
} from "@common/utils/summer2026"
import { ChangePlanBottomModalSection as Section } from "../../../../new-subscription/v2/components/ChangePlanBottomModalSection"
import React, {
    useContext,
    useEffect,
    useImperativeHandle,
    useMemo,
    useState,
} from "react"
import {
    KeyboardAvoidingView,
    Modal,
    Platform,
    ScrollView,
    StyleSheet,
    TouchableOpacity,
    View,
} from "react-native"
import { Portal } from "react-native-paper"
import FontAwesome5 from "react-native-vector-icons/FontAwesome5"
import { connect } from "react-redux"

const HeroBadge = ({
    label = "Offre phare",
    color = theme.colors.summerDeep,
}) => (
    <View style={[styles.heroBadge, { backgroundColor: color }]}>
        <FontAwesome5 name={"star"} solid size={9} color={theme.colors.white} />
        <TextM fontSize={9} fontWeight={"Bold"} color={theme.colors.white}>
            {label}
        </TextM>
    </View>
)

// Options statiques (icônes hardcoded, theme colors via styles globaux) —
// extraites au module-level pour éviter le `useMemo([])` qui ré-évalue
// sans bénéfice et déclenche un warning exhaustive-deps.
const MODE_OPTIONS = [
    {
        label: "Cours d'été",
        value: SUMMER_MONTH_CHOICE.SUMMER_COURSES,
        description: "Programme personnalisé avec un tuteur",
        topChild: (
            <FontAwesome5
                name={"sun"}
                solid
                size={16}
                color={theme.colors.summer}
            />
        ),
        floatingBadge: <HeroBadge label="Le plus choisi" />,
    },
    {
        label: "Mode Autonome",
        value: SUMMER_MONTH_CHOICE.AUTONOMY,
        description: "Plateforme + IA + 1 cours mensuel offert",
        topChild: (
            <FontAwesome5
                name={"user-graduate"}
                size={16}
                color={theme.colors.primary}
            />
        ),
    },
    {
        label: "Pause",
        value: SUMMER_MONTH_CHOICE.PAUSE,
        description: "Aucun prélèvement, aucune séance",
        topChild: (
            <FontAwesome5
                name={"pause-circle"}
                size={16}
                color={theme.colors.grey}
            />
        ),
    },
]

const OFFER_TYPE_OPTIONS = [
    {
        label: "Cours partagés",
        value: SUMMER_COURSES_OFFER_TYPE.SHARED,
        description: "2-3 élèves ensemble · pack 3 créneaux à -50%",
        topChild: (
            <FontAwesome5
                name={"users"}
                size={16}
                color={theme.colors.primary}
            />
        ),
        floatingBadge: <HeroBadge label="Le plus choisi" />,
    },
    {
        label: "Cours particuliers",
        value: SUMMER_COURSES_OFFER_TYPE.PRIVATE,
        description: "1 élève avec son tuteur · 1 à 5h/sem",
        topChild: (
            <FontAwesome5
                name={"user-alt"}
                size={16}
                color={theme.colors.primary}
            />
        ),
    },
]

const PRIVATE_HOURS_OPTIONS = SUMMER_COURSES_PRIVATE_HOURS_PER_WEEK.map(
    (h) => ({
        label: `${h}h`,
        value: h,
    })
)

// Grille de sélection des créneaux partagés : une ligne par jour, chaque
// ligne portant ses créneaux matin (☀) et soir (🌙) togglables — plus
// naturel qu'une liste à plat de chips.
// `rows` porte les créneaux PROPOSABLES (les saturés en sont retirés, sauf ceux
// que la famille détient déjà) — cf. `getSummerSharedTimeslotsByDay`.
const DayTimeslotGrid = ({ rows, selected, onToggle, isNarrow }) => (
    <View style={{ gap: 8 }}>
        {rows.map((row) => (
            <View
                key={row.dayLabel}
                style={{ flexDirection: "row", alignItems: "center" }}
            >
                <TextM
                    fontSize={13}
                    fontWeight={"SemiBold"}
                    style={{ width: isNarrow ? 62 : 84 }}
                >
                    {row.dayLabel}
                </TextM>
                <View style={{ flexDirection: "row", flex: 1, gap: 8 }}>
                    {row.options.map((opt) => {
                        const isSelected = selected.includes(opt.slot)
                        return (
                            <TouchableOpacity
                                key={opt.slot}
                                onPress={() => onToggle(opt.slot)}
                                activeOpacity={0.7}
                                style={{
                                    flex: 1,
                                    alignItems: "center",
                                    justifyContent: "center",
                                    paddingVertical: 9,
                                    paddingHorizontal: 6,
                                    borderRadius: 10,
                                    borderWidth: 1.5,
                                    borderColor: isSelected
                                        ? theme.colors.summer
                                        : theme.colors.lightMore,
                                    backgroundColor: isSelected
                                        ? theme.colors.summerLightDeep
                                        : theme.colors.white,
                                }}
                            >
                                <TextM
                                    fontSize={12}
                                    fontWeight={isSelected ? "Bold" : "Regular"}
                                    color={
                                        isSelected
                                            ? theme.colors.summerDeep
                                            : theme.colors.grey
                                    }
                                >
                                    {opt.timeLabel}
                                </TextM>
                            </TouchableOpacity>
                        )
                    })}
                </View>
            </View>
        ))}
    </View>
)

// `summerConfig` sert au seul défaut des créneaux : il doit être EN VENTE d'après
// la config servie, sinon on cocherait un créneau que la grille masque.
const sanitizeInitialCoursesConfig = (raw, defaultSubjectIds, summerConfig) => {
    const baseSubjects =
        defaultSubjectIds && defaultSubjectIds.length > 0
            ? defaultSubjectIds
            : []
    const defaultSlots = getSummerCoursesDefaultSlots(summerConfig)
    if (!raw) {
        return {
            ...SUMMER_COURSES_DEFAULT_CONFIG,
            slots_possible: [...defaultSlots],
            subjectIds: baseSubjects.slice(0, defaultSlots.length),
        }
    }
    const offerType =
        raw.offerType === SUMMER_COURSES_OFFER_TYPE.PRIVATE
            ? SUMMER_COURSES_OFFER_TYPE.PRIVATE
            : SUMMER_COURSES_OFFER_TYPE.SHARED
    if (offerType === SUMMER_COURSES_OFFER_TYPE.SHARED) {
        const slotsPossible =
            Array.isArray(raw.slots_possible) && raw.slots_possible.length > 0
                ? raw.slots_possible.filter((slot) =>
                      SUMMER_SHARED_TIMESLOT_SLOTS.includes(slot)
                  )
                : [...defaultSlots]
        return {
            offerType,
            slots_possible: slotsPossible,
            hoursPerWeek: null,
            subjectIds: (raw.subjectIds?.length
                ? raw.subjectIds
                : baseSubjects
            ).slice(0, slotsPossible.length),
        }
    }
    const hoursPerWeek =
        raw.hoursPerWeek &&
        SUMMER_COURSES_PRIVATE_HOURS_PER_WEEK.includes(raw.hoursPerWeek)
            ? raw.hoursPerWeek
            : 2
    return {
        offerType,
        slots_possible: [],
        hoursPerWeek,
        subjectIds: (raw.subjectIds?.length
            ? raw.subjectIds
            : baseSubjects
        ).slice(0, hoursPerWeek),
    }
}

// =====================================================================
// SummerMonthEditModal — BottomSheet inspiré de ChangePlanBottomModal,
// dédié au choix du mode estival pour un mois donné. Cours d'été se
// configure soit en créneaux fixes (cours partagés lundi/mardi/mercredi
// 10h30-11h30) soit en heures libres (cours particuliers 1-5h). Soft
// validation : Valider toujours cliquable, missing fields en rouge.
// =====================================================================
const SummerMonthEditModal = ({
    month,
    bottomSheetRef,
    initialSlot,
    schoolSubjects,
    defaultSubjectIds,
    user = null,
    summerConfig = null,
    onValidate,
    onClose,
}) => {
    const dimensions = useContext(DimensionsContext)
    const systemBarInsets = useSystemBarInsets()

    // Card peek pattern on narrow screens: each card takes ~75% of the
    // viewport so the next card visibly overflows on the right, signaling
    // that the row is horizontally scrollable. On wide screens (web/desktop)
    // we keep the minWidth-driven layout that aligns cards left-to-right.
    const isNarrow = (dimensions?.width ?? 0) <= 800
    const peekCardWidth = isNarrow
        ? Math.round((dimensions?.width ?? 360) * 0.75)
        : null

    const initialChoice =
        (initialSlot && typeof initialSlot !== "string"
            ? initialSlot.choice
            : initialSlot) ?? null
    const initialCoursesConfigRaw =
        initialSlot && typeof initialSlot !== "string"
            ? (initialSlot.coursesConfig ?? null)
            : null

    const [choice, setChoice] = useState(initialChoice)
    const [coursesConfig, setCoursesConfig] = useState(() =>
        sanitizeInitialCoursesConfig(
            initialCoursesConfigRaw,
            defaultSubjectIds,
            summerConfig
        )
    )
    const [submitAttempted, setSubmitAttempted] = useState(false)

    // Créneaux que la famille détient DÉJÀ à l'ouverture. Un créneau saturé n'est
    // plus proposé, SAUF s'il est là-dedans : la famille doit voir le sien et
    // pouvoir le décocher/re-cocher pendant son édition (un chip qui s'évapore au
    // décochage serait déroutant). Le set est figé à l'ouverture — une fois le
    // créneau libéré et validé, il ne réapparaît plus à la réouverture.
    const keptSlots = useMemo(() => {
        const raw = initialCoursesConfigRaw?.slots_possible
        return Array.isArray(raw) ? raw : []
    }, [initialCoursesConfigRaw])

    const timeslotRows = useMemo(
        () => getSummerSharedTimeslotsByDay(summerConfig, keptSlots),
        [summerConfig, keptSlots]
    )

    useEffect(() => {
        setChoice(initialChoice)
        setCoursesConfig(
            sanitizeInitialCoursesConfig(
                initialCoursesConfigRaw,
                defaultSubjectIds,
                summerConfig
            )
        )
        setSubmitAttempted(false)
    }, [initialChoice, initialCoursesConfigRaw, defaultSubjectIds, summerConfig])

    const subjectOptions = useMemo(
        () =>
            (schoolSubjects || []).map((s) => ({
                label: s.name,
                value: s.id,
                labelIconImage: s.icon,
            })),
        [schoolSubjects]
    )

    const priceDescriptor =
        choice === SUMMER_MONTH_CHOICE.SUMMER_COURSES
            ? getSummerCoursesPriceDescriptor(coursesConfig, summerConfig)
            : null

    const isShared =
        coursesConfig.offerType === SUMMER_COURSES_OFFER_TYPE.SHARED
    const hoursPerWeek = getSummerCoursesHoursPerWeek(coursesConfig)

    // Validation invariants — utilisés pour le soft-error highlighting.
    const isChoiceMissing = !choice
    const isTimeslotsMissing =
        choice === SUMMER_MONTH_CHOICE.SUMMER_COURSES &&
        isShared &&
        coursesConfig.slots_possible.length === 0
    const isHoursMissing =
        choice === SUMMER_MONTH_CHOICE.SUMMER_COURSES &&
        !isShared &&
        !coursesConfig.hoursPerWeek
    const isSubjectsMissing =
        choice === SUMMER_MONTH_CHOICE.SUMMER_COURSES &&
        coursesConfig.subjectIds.length === 0

    const hasErrors =
        isChoiceMissing ||
        isTimeslotsMissing ||
        isHoursMissing ||
        isSubjectsMissing

    const handleSelectMode = (next) => {
        setChoice(next)
        setSubmitAttempted(false)
    }

    const handleSelectOfferType = (next) => {
        setCoursesConfig((prev) => {
            if (next === SUMMER_COURSES_OFFER_TYPE.SHARED) {
                const defaultSlots = getSummerCoursesDefaultSlots(summerConfig)
                return {
                    offerType: next,
                    slots_possible: [...defaultSlots],
                    hoursPerWeek: null,
                    subjectIds: prev.subjectIds.slice(0, defaultSlots.length),
                }
            }
            // Switching Shared → Private : on calcule d'abord le hoursPerWeek
            // résultant (fallback 2h) puis on slice les matières sur cette
            // valeur (et non sur prev.hoursPerWeek qui peut être null en
            // venant de Shared, ce qui produirait une perte silencieuse).
            const nextHoursPerWeek = prev.hoursPerWeek || 2
            return {
                offerType: next,
                slots_possible: [],
                hoursPerWeek: nextHoursPerWeek,
                subjectIds: prev.subjectIds.slice(0, nextHoursPerWeek),
            }
        })
    }

    const handleSelectTimeslots = (slots) => {
        setCoursesConfig((prev) => ({
            ...prev,
            slots_possible: slots,
            subjectIds: prev.subjectIds.slice(0, slots.length),
        }))
    }

    // Toggle a single créneau, keeping the selection in canonical config order
    // (filter over the catalogue) so it never depends on click order.
    const toggleTimeslot = (slot) => {
        const current = coursesConfig.slots_possible
        const next = current.includes(slot)
            ? current.filter((s) => s !== slot)
            : SUMMER_SHARED_TIMESLOT_SLOTS.filter(
                  (s) => s === slot || current.includes(s)
              )
        handleSelectTimeslots(next)
    }

    const handleSelectPrivateHours = (h) => {
        setCoursesConfig((prev) => ({
            ...prev,
            hoursPerWeek: h,
            subjectIds: prev.subjectIds.slice(0, h),
        }))
    }

    const handleSelectSubjects = (ids) => {
        setCoursesConfig((prev) => ({ ...prev, subjectIds: ids }))
    }

    const handleValidate = () => {
        if (hasErrors) {
            setSubmitAttempted(true)
            return
        }
        const payload =
            choice === SUMMER_MONTH_CHOICE.SUMMER_COURSES
                ? { choice, coursesConfig }
                : { choice, coursesConfig: null }
        onValidate?.(payload)
        onClose?.()
    }

    const showError = (flag) => submitAttempted && flag

    const [visible, setVisible] = useState(false)
    useImperativeHandle(
        bottomSheetRef,
        () => ({
            present: () => setVisible(true),
            dismiss: () => setVisible(false),
        }),
        []
    )

    const isLargeScreen = (dimensions?.width ?? 0) > 800

    const handleRequestClose = () => {
        setVisible(false)
        onClose?.()
    }

    const headerBlock = (
        <View style={styles.header}>
            <View style={{ flex: 1, alignItems: "center" }}>
                <TextM fontSize={20} fontWeight={"Bold"} center>
                    {(() => {
                        const label = getSummerMonthLabel(month).toLowerCase()
                        // French elision before vowel/h: "Mois d'août".
                        const startsVowel = /^[aeiouhâéèêîôù]/i.test(label)
                        return startsVowel
                            ? `Mois d'${label}`
                            : `Mois de ${label}`
                    })()}
                </TextM>
                <TextM
                    fontSize={12}
                    color={theme.colors.grey}
                    center
                    style={{ marginTop: 4 }}
                >
                    {getSummerMonthDateRange(month)}
                </TextM>
            </View>
            <IconButtonM
                icon={"times"}
                size={22}
                iconColor={theme.colors.primary}
                style={styles.closeButton}
                onPress={handleRequestClose}
            />
        </View>
    )

    const content = (
        <KeyboardAvoidingView
            behavior={Platform.OS === "ios" ? "padding" : "height"}
            style={{
                flex: isLargeScreen ? undefined : 1,
                backgroundColor: theme.colors.white,
                borderRadius: isLargeScreen ? 12 : 0,
                height: isLargeScreen ? "90%" : undefined,
                width: isLargeScreen
                    ? Math.min(640, dimensions?.width ?? 640)
                    : undefined,
                overflow: "hidden",
            }}
        >
            {headerBlock}
            <ScrollView contentContainerStyle={styles.content}>
                <SectionWrap
                    icon={"sun"}
                    title={"Mode pour ce mois"}
                    error={showError(isChoiceMissing)}
                    errorText={"Choisissez un mode pour ce mois"}
                >
                    <TabSelect
                        options={MODE_OPTIONS}
                        selected={choice}
                        onSelect={handleSelectMode}
                        cardStyle={
                            isNarrow
                                ? { width: peekCardWidth }
                                : { flex: 1, minWidth: 220 }
                        }
                    />
                </SectionWrap>

                {choice === SUMMER_MONTH_CHOICE.SUMMER_COURSES ? (
                    <>
                        <HeroMarketingBlock priceDescriptor={priceDescriptor} />

                        <SectionWrap icon={"users"} title={"Mode des séances"}>
                            <TabSelect
                                options={OFFER_TYPE_OPTIONS}
                                selected={coursesConfig.offerType}
                                onSelect={handleSelectOfferType}
                                cardStyle={
                                    isNarrow
                                        ? { width: peekCardWidth }
                                        : { flex: 1, minWidth: 320 }
                                }
                            />
                        </SectionWrap>

                        {isShared ? (
                            <SectionWrap
                                icon={"calendar-alt"}
                                title={"Créneaux hebdo"}
                                error={showError(isTimeslotsMissing)}
                                errorText={"Sélectionnez au moins un créneau"}
                                helper={
                                    coursesConfig.slots_possible.length === 3
                                        ? "Pack 3 créneaux · -50% activé"
                                        : "Le pack 3 créneaux passe à 89€/mois"
                                }
                            >
                                <DayTimeslotGrid
                                    rows={timeslotRows}
                                    selected={coursesConfig.slots_possible}
                                    onToggle={toggleTimeslot}
                                    isNarrow={isNarrow}
                                />
                            </SectionWrap>
                        ) : (
                            <SectionWrap
                                icon={"clock"}
                                title={"Heures par semaine"}
                                error={showError(isHoursMissing)}
                                errorText={"Choisissez un nombre d'heures"}
                            >
                                <TabSelect
                                    options={PRIVATE_HOURS_OPTIONS}
                                    selected={coursesConfig.hoursPerWeek}
                                    onSelect={handleSelectPrivateHours}
                                    cardStyle={{
                                        minWidth: 80,
                                        alignItems: "center",
                                    }}
                                />
                            </SectionWrap>
                        )}

                        <SectionWrap
                            icon={"book"}
                            title={"Matières souhaitées"}
                            error={showError(isSubjectsMissing)}
                            errorText={"Choisissez au moins une matière"}
                            helper={
                                hoursPerWeek > 0
                                    ? `Jusqu'à ${hoursPerWeek} matière${
                                          hoursPerWeek > 1 ? "s" : ""
                                      }, 1 séance hebdo par matière`
                                    : "Sélectionnez d'abord vos créneaux"
                            }
                        >
                            <TabSelect
                                multiple
                                options={subjectOptions}
                                selected={coursesConfig.subjectIds}
                                onSelect={handleSelectSubjects}
                                maxSelection={hoursPerWeek || null}
                                cardStyle={{ borderRadius: 30 }}
                            />
                        </SectionWrap>
                    </>
                ) : null}

                {choice === SUMMER_MONTH_CHOICE.AUTONOMY ? (
                    <View style={styles.infoBox}>
                        <FontAwesome5
                            name={"user-graduate"}
                            solid
                            size={18}
                            color={theme.colors.primary}
                            style={{ marginRight: 10 }}
                        />
                        <TextM fontSize={13} wrap style={{ flex: 1 }}>
                            Plateforme illimitée + IA pédagogique + 1 cours
                            tuteur offert sur le mois.
                        </TextM>
                    </View>
                ) : null}

                {choice === SUMMER_MONTH_CHOICE.PAUSE ? (
                    <View style={styles.infoBox}>
                        <FontAwesome5
                            name={"pause-circle"}
                            solid
                            size={18}
                            color={theme.colors.grey}
                            style={{ marginRight: 10 }}
                        />
                        <View style={{ flex: 1 }}>
                            <TextM fontSize={13} wrap>
                                Aucun prélèvement, aucune séance, reprise
                                automatique de l&apos;abonnement à la rentrée.
                            </TextM>
                            <ModalPauseCommitmentShiftLine
                                user={user}
                                month={month}
                            />
                        </View>
                    </View>
                ) : null}
            </ScrollView>

            <View style={styles.footer}>
                {choice === SUMMER_MONTH_CHOICE.SUMMER_COURSES &&
                priceDescriptor ? (
                    <PriceFooterCard
                        descriptor={priceDescriptor}
                        isHero={priceDescriptor.isHeroOffer}
                    />
                ) : null}

                {choice === SUMMER_MONTH_CHOICE.AUTONOMY ? (
                    <View style={styles.priceCard}>
                        <TextM fontWeight={"Bold"} fontSize={16}>
                            {SUMMER_PRICES.AUTONOMY_MONTHLY_PRICE}€ pour ce mois
                        </TextM>
                    </View>
                ) : null}

                {choice === SUMMER_MONTH_CHOICE.PAUSE ? (
                    <View style={styles.priceCard}>
                        <TextM fontWeight={"Bold"} fontSize={16}>
                            Aucun prélèvement
                        </TextM>
                    </View>
                ) : null}

                <FilledButtonM
                    label={"Choisir"}
                    onPress={handleValidate}
                    style={{ width: "100%", marginBottom: 0 }}
                />
            </View>
        </KeyboardAvoidingView>
    )

    if (isLargeScreen) {
        return (
            <Portal>
                <Modal
                    visible={visible}
                    transparent
                    animationType={"fade"}
                    onRequestClose={handleRequestClose}
                >
                    <View
                        style={{
                            flex: 1,
                            backgroundColor: theme.colors.blackTransparent2,
                            justifyContent: "center",
                            alignItems: "center",
                        }}
                    >
                        <TouchableOpacity
                            style={{
                                position: "absolute",
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                            }}
                            activeOpacity={1}
                            onPress={handleRequestClose}
                        />
                        {content}
                    </View>
                </Modal>
            </Portal>
        )
    }

    return (
        <Modal
            visible={visible}
            transparent
            animationType={"slide"}
            onRequestClose={handleRequestClose}
        >
            <View
                style={{
                    flex: 1,
                    backgroundColor: theme.colors.blackTransparent2,
                    justifyContent: "flex-end",
                }}
            >
                <View
                    style={{
                        height: "90%",
                        backgroundColor: theme.colors.white,
                        borderTopLeftRadius: 16,
                        borderTopRightRadius: 16,
                        overflow: "hidden",
                        // A modal is a window of its own, drawn behind
                        // Android's navigation buttons.
                        paddingBottom: systemBarInsets.bottom,
                    }}
                >
                    {content}
                </View>
            </View>
        </Modal>
    )
}

// Affiché sous l'info "aucun prélèvement" quand le parent choisit Pause :
// rappelle que son engagement courant sera décalé du nombre de jours pausés
// (mirroir de la pause manuelle, cf. `ParentPauseSubscriptionModal` étape 2).
// Lit `user.commitment_ends_at` ; si l'engagement est passé/absent, n'affiche
// rien. Le mois courant (`month`) cap le calcul à ~31 jours par carte ; le
// total cumulé "juillet + août = 62 jours" est surfacé séparément par le
// récap-card côté `SummerMonthPlanCard`.
const ModalPauseCommitmentShiftLine = ({ user, month }) => {
    const shift = computeSummerPauseCommitmentShiftForMonth(user, month)
    if (!shift.hasCommitment || !shift.days) return null
    return (
        <TextM
            fontSize={12}
            wrap
            color={theme.colors.grey}
            style={{ marginTop: 6, lineHeight: 17 }}
        >
            {"L'engagement, qui se termine actuellement le "}
            <TextM fontWeight={"Bold"} fontSize={12}>
                {shift.oldCommitment.format("DD/MM/YYYY")}
            </TextM>
            {", sera décalé au "}
            <TextM fontWeight={"Bold"} fontSize={12}>
                {shift.newCommitment.format("DD/MM/YYYY")}
            </TextM>
            {` après la pause (${shift.days} jours).`}
        </TextM>
    )
}

const SectionWrap = ({ icon, title, error, errorText, helper, children }) => (
    <View style={[styles.sectionWrap, error && styles.sectionWrapError]}>
        <Section icon={icon} title={title}>
            {children}
        </Section>
        {error ? (
            <TextM
                fontSize={11}
                color={theme.colors.error}
                wrap
                style={{ marginTop: 4 }}
            >
                {errorText}
            </TextM>
        ) : helper ? (
            <TextM
                fontSize={11}
                color={theme.colors.light}
                wrap
                style={{ marginTop: 4 }}
            >
                {helper}
            </TextM>
        ) : null}
    </View>
)

// Mise en avant marketing — affichée uniquement quand le mode Cours
// d'été est actif, pour rappeler la promesse phare (-50% sur le pack
// complet, IA illimité, suivi).
const HeroMarketingBlock = ({ priceDescriptor }) => (
    <View style={styles.heroBlock}>
        <View style={styles.heroBlockHeader}>
            <FontAwesome5
                name={"sun"}
                solid
                size={14}
                color={theme.colors.summerDeep}
                style={{ marginRight: 6 }}
            />
            <TextM
                fontWeight={"ExtraBold"}
                fontSize={12}
                color={theme.colors.summerDeep}
                uppercase
                style={{ letterSpacing: 0.4 }}
            >
                L&apos;offre phare de l&apos;été
            </TextM>
            <View style={styles.heroDiscount}>
                <TextM
                    fontWeight={"ExtraBold"}
                    fontSize={11}
                    color={theme.colors.white}
                >
                    −50% pack 3 créneaux
                </TextM>
            </View>
        </View>
        <TextM
            fontSize={13}
            color={theme.colors.text}
            wrap
            style={{ marginTop: 8 }}
        >
            Programme construit avec un tuteur expert · énoncés illimités + IA
            pédagogique · cours en petit groupe.
        </TextM>
        {priceDescriptor?.isHeroOffer ? (
            <TextM
                fontSize={12}
                color={theme.colors.summerDeep}
                fontWeight={"Bold"}
                wrap
                style={{ marginTop: 6 }}
            >
                Pack complet 3h/sem activé · {priceDescriptor.monthlyPrice}€ au
                lieu de {priceDescriptor.flexEquivalentPrice}€ — économie{" "}
                {priceDescriptor.savings}€/mois.
            </TextM>
        ) : null}
    </View>
)

const PriceFooterCard = ({ descriptor, isHero }) => (
    <View style={[styles.priceCard, isHero && styles.priceCardHero]}>
        <View style={{ flex: 1 }}>
            <TextM
                fontSize={12}
                color={isHero ? theme.colors.summerDeep : theme.colors.light}
                fontWeight={"Medium"}
            >
                {descriptor.hoursPerMonth} h / mois
            </TextM>
            <View style={styles.priceRow}>
                {descriptor.flexEquivalentPrice > descriptor.monthlyPrice ? (
                    <TextM
                        fontSize={14}
                        color={theme.colors.grey}
                        style={{
                            textDecorationLine: "line-through",
                            marginRight: 8,
                        }}
                    >
                        {descriptor.flexEquivalentPrice}€
                    </TextM>
                ) : null}
                <TextM
                    fontWeight={"Bold"}
                    fontSize={24}
                    color={
                        isHero ? theme.colors.summerDeep : theme.colors.primary
                    }
                >
                    {descriptor.monthlyPrice}€
                </TextM>
                <TextM
                    fontSize={12}
                    color={theme.colors.grey}
                    style={{ marginLeft: 4 }}
                >
                    / mois
                </TextM>
            </View>
            <TextM
                fontSize={11}
                color={isHero ? theme.colors.summerDeep : theme.colors.light}
                style={{ marginTop: 2 }}
            >
                Soit {descriptor.hourlyPrice}€/h
                {descriptor.savings > 0
                    ? ` · économie ${descriptor.savings}€/mois vs abo actuel`
                    : ""}
            </TextM>
        </View>
    </View>
)

const styles = StyleSheet.create({
    header: {
        padding: 16,
        borderBottomWidth: 1,
        borderBottomColor: theme.colors.lightMore,
        flexDirection: "row",
        alignItems: "center",
    },
    closeButton: {
        position: "absolute",
        right: 8,
        top: 10,
    },
    content: {
        padding: 16,
        gap: 20,
    },
    footer: {
        padding: 16,
        borderTopWidth: 1,
        borderTopColor: theme.colors.lightMore,
        gap: 12,
    },
    priceCard: {
        backgroundColor: theme.colors.lightMoreTransparent,
        padding: 16,
        borderRadius: 10,
        flexDirection: "row",
        alignItems: "center",
    },
    priceCardHero: {
        backgroundColor: theme.colors.summerLight,
        borderWidth: 1,
        borderColor: theme.colors.summerBorder,
    },
    priceRow: {
        flexDirection: "row",
        alignItems: "baseline",
        marginTop: 4,
    },
    sectionWrap: {
        gap: 4,
    },
    sectionWrapError: {
        borderLeftWidth: 3,
        borderLeftColor: theme.colors.error,
        paddingLeft: 10,
    },
    heroBlock: {
        backgroundColor: theme.colors.summerLight,
        borderColor: theme.colors.summerBorder,
        borderWidth: 1,
        borderRadius: 12,
        padding: 14,
    },
    heroBlockHeader: {
        flexDirection: "row",
        alignItems: "center",
        flexWrap: "wrap",
        gap: 6,
    },
    heroDiscount: {
        backgroundColor: theme.colors.error,
        paddingHorizontal: 10,
        paddingVertical: 3,
        borderRadius: 999,
        marginLeft: 4,
    },
    heroBadge: {
        paddingHorizontal: 10,
        paddingVertical: 3,
        borderRadius: 10,
        flexDirection: "row",
        alignItems: "center",
        gap: 4,
    },
    infoBox: {
        flexDirection: "row",
        alignItems: "center",
        backgroundColor: theme.colors.lightMoreTransparent,
        padding: 14,
        borderRadius: 10,
    },
})

const mapStateToProps = (state) => ({
    schoolSubjects: state.data.formData?.schoolSubjects,
    user: state.data.formData?.user,
    summerConfig: state.data.staticData?.summer_config,
})

export default connect(mapStateToProps)(SummerMonthEditModal)
